import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Checkbox } from '@mui/material';
import { Close, Check } from '@mui/icons-material';
import { get, set } from '@totalsoft/change-tracking-react';
import { useTranslation } from 'react-i18next';
import { Autocomplete, IconButton } from '@totalsoft/rocket-ui';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';
import { usePermission } from 'providers/RightsHooks';
import { StyledTableBodyCell, StyledTableRow } from 'components/common/Styles';
import { ADMIN_INSURANCE_CONVENTIONS_WRITE } from 'constants/rightsCodes';
import FlexBox from 'components/common/FlexBox';

const InsurerDetails = ({ index, insurer, insurers, onEdit, onCancelEditInsurer, onSaveDetail, onDelete, validation }) => {
  const { t } = useTranslation();
  const canWrite = usePermission(ADMIN_INSURANCE_CONVENTIONS_WRITE);

  const insurerChange = useCallback(
    value => {
      set(insurer.editObject.insurerId)(value.id);
      set(insurer.editObject.insurerName)(value.name);
    },
    [insurer]
  );

  return (
    <StyledTableRow hover key={insurer.insurerId |> get}>
      <StyledTableBodyCell>
        {insurer.inEdit |> get ? (
          <Autocomplete
            fullWidth
            value={{ id: insurer.editObject.insurerId |> get, name: insurer.editObject.insurerName |> get }}
            onChange={insurerChange}
            required
            valueKey='id'
            labelKey='name'
            options={insurers}
            label={t('InsuranceConventions.Details.Insurers.Insurer')}
            disabled={
              ((insurer.editObject.insurerId |> get) !== null && (insurer.insurerId |> get) !== null) ||
              (insurer.disabled |> get) ||
              !canWrite
            }
            error={!isValid(validation[index].editObject.insurerId)}
            helperText={getErrors(validation[index].editObject.insurerId)}
          />
        ) : (
          <>{insurer.insurerName |> get}</>
        )}
      </StyledTableBodyCell>
      <StyledTableBodyCell>
        {insurer.inEdit |> get ? (
          <Checkbox
            disabled={!canWrite}
            checked={get(insurer.editObject.isDefault) || false}
            onChange={() => set(insurer.editObject.isDefault, !get(insurer.editObject.isDefault))}
          />
        ) : (
          <>{(insurer.isDefault |> get) === true ? <Check color='success' /> : <Close />}</>
        )}
      </StyledTableBodyCell>
      <StyledTableBodyCell>
        <FlexBox justifyContent='end' gap={0.5}>
          {insurer.inEdit |> get ? (
            <>
              <IconButton
                type='save'
                color='primary'
                size='tiny'
                variant='text'
                onClick={() => {
                  return onSaveDetail(insurer, index);
                }}
                disabled={!insurer.inEdit || !(insurer.editObject.insurerId |> get) || (insurer.disabled |> get) || !canWrite}
                tooltip={t('Save')}
              />
              <IconButton
                type='cancel'
                color='primary'
                size='tiny'
                variant='text'
                onClick={() => {
                  onCancelEditInsurer(insurer, index);
                }}
                tooltip={t('Cancel')}
                disabled={(insurer.disabled |> get) || !canWrite}
              />
            </>
          ) : (
            <IconButton
              type='edit'
              color='primary'
              size='tiny'
              variant='text'
              onClick={() => onEdit(insurer)}
              tooltip={t('Edit')}
              disabled={!canWrite}
            />
          )}
          <IconButton
            type='delete'
            color='primary'
            size='tiny'
            variant='text'
            onClick={() => onDelete(insurer.insurerId |> get)}
            disabled={(insurer.insurerId |> get) === null || (insurer.disabled |> get) || !canWrite}
            tooltip={t('Delete')}
          />
        </FlexBox>
      </StyledTableBodyCell>
    </StyledTableRow>
  );
};

InsurerDetails.propTypes = {
  index: PropTypes.number,
  insurer: PropTypes.object,
  insurers: PropTypes.array,
  onEdit: PropTypes.func,
  onCancelEditInsurer: PropTypes.func,
  onSaveDetail: PropTypes.func,
  onDelete: PropTypes.func,
  validation: PropTypes.object
};

export default InsurerDetails;
